import { NestedScrollView } from '@/shared/components';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tab } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Member } from '../../models/member';
import { MemberList } from '../conversation-member/conversation-member';
import { FileTabPanel } from './file-tab-panel';
import { LinkTabPanel } from './link-tab-panel';
import { MediaTabPanel } from './media-tab-panel';

export interface ConverstationTabsProps {
  conversationId: string;
  hideMember: boolean;
  members: Member[];
  memberCount: number;
  curUser: Member;
  onTabChange?: (tabKey: string) => void;
  onPanelScrollUp?: () => void;
  onPanelScrollDown?: () => void;
}

const ConverstationTabs: FC<ConverstationTabsProps> = ({
  conversationId,
  hideMember,
  members,
  memberCount,
  curUser,
  onTabChange,
  onPanelScrollUp,
  onPanelScrollDown
}) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(hideMember ? 'media' : 'member');

  const CONVERSATION_TABS = [
    { key: 'member', label: t('conversations.tabs.member') },
    { key: 'media', label: t('conversations.tabs.media') },
    { key: 'file', label: t('conversations.tabs.file') },
    { key: 'link', label: t('conversations.tabs.link') }
  ] as const;

  useEffect(() => {
    if (hideMember) {
      setTabIndex('media');
    } else {
      setTabIndex('member');
      onTabChange?.('member');
    }
  }, [hideMember, conversationId]);

  return (
    <div className='mex:flex mex:h-full mex:min-h-0 mex:flex-col'>
      <TabContext value={tabIndex}>
        <TabList
          component='div'
          variant='scrollable'
          className='mex:border-divider mex:border-b mex:px-2'
          onChange={(_, value) => {
            setTabIndex(value);
            onTabChange?.(value);
          }}
        >
          {CONVERSATION_TABS.filter((tab) => !hideMember || tab.key !== 'member').map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </TabList>
        {!hideMember && (
          <TabPanel value='member' sx={{ flex: 1, padding: 0, paddingTop: 1, display: 'flex', minHeight: 0 }}>
            <NestedScrollView
              className='mex:min-h-0 mex:flex-1'
              onScrollUp={onPanelScrollUp}
              onScrollDown={onPanelScrollDown}
            >
              <MemberList members={members} memberCount={memberCount} convId={conversationId} curUser={curUser} />
            </NestedScrollView>
          </TabPanel>
        )}
        <TabPanel value='media' sx={{ flex: 1, padding: 0, paddingTop: 1, display: 'flex', minHeight: 0 }}>
          <NestedScrollView
            className='mex:min-h-0 mex:flex-1'
            onScrollUp={onPanelScrollUp}
            onScrollDown={onPanelScrollDown}
          >
            <MediaTabPanel conversationId={conversationId} />
          </NestedScrollView>
        </TabPanel>
        <TabPanel value='file' sx={{ flex: 1, padding: 0, paddingTop: 1, display: 'flex', minHeight: 0 }}>
          <NestedScrollView
            className='mex:min-h-0 mex:flex-1'
            onScrollUp={onPanelScrollUp}
            onScrollDown={onPanelScrollDown}
          >
            <FileTabPanel conversationId={conversationId} />
          </NestedScrollView>
        </TabPanel>
        <TabPanel value='link' sx={{ flex: 1, padding: 0, paddingTop: 1, display: 'flex', minHeight: 0 }}>
          <NestedScrollView
            className='mex:min-h-0 mex:flex-1'
            onScrollUp={onPanelScrollUp}
            onScrollDown={onPanelScrollDown}
          >
            <LinkTabPanel conversationId={conversationId} />
          </NestedScrollView>
        </TabPanel>
      </TabContext>
    </div>
  );
};

export default ConverstationTabs;
